import { Activity } from 'src/services/activities/activities.types';

import { rootApi } from '../rootApi';
import { SortOrder } from '../common.types';
import { Contact } from '../contacts/contacts.types';
import { orgBasePath } from '../organisation/organisation.service';
import { JobApplication } from '../applications/applications.types';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  Connection,
  GetProfileById,
  ICompanyProfile,
  ICompanyDocument,
  ICompanyListItem,
  ConnectionRequest,
  CompanySearchProps,
  IContactCompanyLink,
  ProfileJobsResponse,
  CompanyImportResult,
  CompanyLeanListProps,
  BulkCompanyImportDto,
  ConnectionSearchProps,
  CreateLocalCompanyDto,
  GetConnectionsRequest,
  UnlinkConnectionProps,
  UpdateLocalCompanyDto,
  CreateCompanyDocumentDto,
  GetConnectionDetailsProps,
  GetConnectionRequestProps,
  GetConnectionResourceProps,
  ICompanyContactLinkListItem,
  UpdateConnectionSettingsPops,
  InitiateConnectionRequestPops,
  ConnectionRequestActionPayload,
} from './connections.types';

export const connectionsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrgConnections: builder.query<PaginatedResponse<Connection>, GetConnectionsRequest>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connections`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
          status: data.status,
        },
      }),
      providesTags: ['Connections'],
    }),

    getConnectionById: builder.query<Connection, { organization_id: string; connection_id: string }>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connections/${data.connection_id}`,
      }),
      providesTags: (_res, _err, args) => [{ type: 'Connections', id: args.connection_id }],
    }),

    getConnectionRequestById: builder.query<ConnectionRequest, GetConnectionDetailsProps>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connection-requests/${data.request_id}`,
      }),
    }),

    actionConnectionRequest: builder.mutation<APIResponse, ConnectionRequestActionPayload>({
      query: (data) => ({
        url: `/connection-requests/${data.connectionId}/action`,
        method: HTTPRequestType.POST,
        data: data.action,
      }),
      invalidatesTags: ['Notifications', 'Connections', 'ConnectionRequests'],
    }),

    initiateConnectionRequest: builder.mutation<ConnectionRequest, InitiateConnectionRequestPops>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connection-requests`,
        method: HTTPRequestType.POST,
        data: data.request,
      }),
      invalidatesTags: ['ConnectionRequests'],
    }),

    updateConnectionSettings: builder.mutation<APIResponse, UpdateConnectionSettingsPops>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connections/${data.connection_id}/settings`,
        method: HTTPRequestType.PUT,
        data: data.settings,
      }),
      invalidatesTags: (_res, _err, args) => ['Connections'],
    }),

    cancelConnectionRequest: builder.mutation<APIResponse, { connectionId: string }>({
      query: (data) => ({
        url: `/connection-requests/${data.connectionId}/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['ConnectionRequests'],
    }),

    getConnectionRequests: builder.query<
      PaginatedResponse<ConnectionRequest>,
      GetConnectionRequestProps
    >({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connection-requests/${data.target}?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: ['ConnectionRequests'],
    }),

    unlinkConnection: builder.mutation<APIResponse, UnlinkConnectionProps>({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connections/${data.connection_id}/disconnect`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Connections'],
    }),

    getIncomingConnectionRequests: builder.query<
      PaginatedResponse<ConnectionRequest>,
      GetConnectionsRequest
    >({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connection-requests/incoming?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: ['ConnectionRequests'],
    }),

    getOutgoingConnectionRequests: builder.query<
      PaginatedResponse<ConnectionRequest>,
      GetConnectionsRequest
    >({
      query: (data) => ({
        url: `/${orgBasePath}/${data.organization_id}/connection-requests/outgoing?page=${data.page
          }&per_page=${data.per_page}${data.status ? `&status=${data.status}` : ''}`,
      }),
      providesTags: ['ConnectionRequests'],
    }),

    getConnectionRequestByToken: builder.query<ConnectionRequest, string>({
      query: (token) => ({
        url: `/connection-requests?identifier=${token}`,
      }),
    }),

    getInteractedOrganisations: builder.query<
      PaginatedResponse<ICompanyProfile>,
      ConnectionSearchProps
    >({
      query: ({ params, target }) => ({
        url: `/my/${target}`,
        params,
      }),
      providesTags: (_res, _err, args) =>
        args.target === 'recruiters' ? ['Recruiters'] : ['Clients'],
    }),

    getConnectionJobs: builder.query<ProfileJobsResponse, GetConnectionResourceProps>({
      query: ({ profile_id, target, params }) => ({
        url: `/my/${target}/${profile_id}/jobs`,
        params,
      }),
    }),

    getConnectionApplications: builder.query<
      PaginatedResponse<JobApplication>,
      GetConnectionResourceProps
    >({
      query: ({ profile_id, target, params }) => ({
        url: `/my/${target}/${profile_id}/job-applications`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'ConnectionApplications', id: args.profile_id }],
    }),

    getConnectionContacts: builder.query<PaginatedResponse<Contact>, GetConnectionResourceProps>({
      query: ({ profile_id, target, params }) => ({
        url: `/my/${target}/${profile_id}/contacts`,
        params,
      }),
    }),

    getConnectionProfileById: builder.query<ICompanyProfile, GetProfileById>({
      query: ({ profile_id, target }) => ({
        url: `/my/${target}/${profile_id}`,
      }),
    }),

    getConnectionActivities: builder.query<PaginatedResponse<Activity>, { connectionId: string; organizationId: string, page: number; perPage: number }>({
      query: (data) => ({
        url: `/organizations/${data.organizationId}/connections/${data.connectionId}/activities`,
        params: {
          page: data.page,
          per_page: data.perPage
        },
      })
    }),

    getMyCompanies: builder.query<PaginatedResponse<ICompanyProfile>, CompanySearchProps>({
      query: ({ params }) => ({
        url: `/my/companies`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'Companies' }],
    }),

    // Lean list read path for the shared companies list (JT1-1527/1528, item 27). Separate from
    // getMyCompanies (other screens depend on its shape); returns flat ICompanyListItem with counts.
    getMyCompaniesLean: builder.query<PaginatedResponse<ICompanyListItem>, CompanyLeanListProps>({
      query: ({ params }) => {
        const { column_filters, ...rest } = params;
        return {
          url: `/my/companies/list`,
          params: {
            ...rest,
            ...(column_filters && Object.keys(column_filters).length ? { column_filters: JSON.stringify(column_filters) } : {}),
          },
        };
      },
      providesTags: () => [{ type: 'Companies' }],
    }),
    getCompanyById: builder.query<ICompanyProfile, { profile_id: string }>({
      query: ({ profile_id }) => ({
        url: `/my/companies/${profile_id}`,
      }),
      providesTags: (_res, _err, args) => [{ type: 'Companies', id: args.profile_id }],
    }),
    getCompanyActivities: builder.query<PaginatedResponse<Activity>, { profile_id: string; page: number; perPage: number }>({
      query: ({ profile_id, page, perPage }) => ({
        url: `/my/companies/${profile_id}/activities`,
        params: { page, per_page: perPage },
      }),
    }),
    getCompanyJobs: builder.query<ProfileJobsResponse, { profile_id: string, params: PageOptions }>({
      query: ({ profile_id, params }) => ({
        url: `/my/companies/${profile_id}/jobs`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyJobs', id: args.profile_id }],
    }),
    getCompanyContacts: builder.query<PaginatedResponse<Contact>, { profile_id: string, params: PageOptions }>({
      query: ({ profile_id, params }) => ({
        url: `/my/companies/${profile_id}/contacts`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyContacts', id: args.profile_id }],
    }),
    getCompanyApplications: builder.query<PaginatedResponse<JobApplication>, { profile_id: string, params: PageOptions }>({
      query: ({ profile_id, params }) => ({
        url: `/my/companies/${profile_id}/job-applications`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyApplications', id: args.profile_id }],
    }),

    getCompanyDocuments: builder.query<PaginatedResponse<ICompanyDocument>, { profile_id: string, params: PageOptions }>({
      query: ({ profile_id, params }) => ({
        url: `/my/companies/${profile_id}/documents`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyDocuments', id: args.profile_id }],
    }),
    createCompanyDocument: builder.mutation<APIResponse, { profile_id: string, data: CreateCompanyDocumentDto }>({
      query: ({ profile_id, data }) => ({
        url: `/my/companies/${profile_id}/documents`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CompanyDocuments', id: args.profile_id }],
    }),
    removeCompanyDocument: builder.mutation<APIResponse, { profile_id: string, company_doc_id: string }>({
      query: ({ profile_id, company_doc_id }) => ({
        url: `/my/companies/${profile_id}/documents/${company_doc_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CompanyDocuments', id: args.profile_id }],
    }),
    // Saves a separate copy of a linked contact's document on the company.
    copyContactDocumentToCompany: builder.mutation<APIResponse, { profile_id: string, contact_document_id: string }>({
      query: ({ profile_id, contact_document_id }) => ({
        url: `/my/companies/${profile_id}/documents/from-contact`,
        method: HTTPRequestType.POST,
        data: { contact_document_id },
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CompanyDocuments', id: args.profile_id }],
    }),

    createLocalCompany: builder.mutation<ICompanyProfile, CreateLocalCompanyDto>({
      query: (data) => ({
        url: `/my/companies`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Companies'],
    }),
    updateLocalCompany: builder.mutation<APIResponse, { profile_id: string, data: UpdateLocalCompanyDto }>({
      query: ({ profile_id, data }) => ({
        url: `/my/companies/${profile_id}`,
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'Companies', id: args.profile_id }],
    }),
    bulkCompanyImport: builder.mutation<CompanyImportResult[], { data: BulkCompanyImportDto }>({
      query: ({ data }) => ({
        url: `/my/companies/csv-import`,
        method: HTTPRequestType.POST,
        data: {
          organization_id: data.organization_id,
          data: data.companies,
          on_duplicate: data.on_duplicate,
          list: data.list,
        },
      }),
      invalidatesTags: ['Companies', 'EntityLists', 'ListItems'],
    }),
    updateCompanyAccountOwner: builder.mutation<APIResponse, { profile_id: string, data: { account_id: string | null } }>({
      query: ({ profile_id, data }) => ({
        url: `/my/companies/${profile_id}/account-owner`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'Companies', id: args.profile_id }],
    }),

    getCompanyLinks: builder.query<PaginatedResponse<IContactCompanyLink>, { profile_id: string, params: PageOptions }>({
      query: ({ profile_id, params }) => ({
        url: `/my/companies/${profile_id}/company-links`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyLink', id: args.profile_id }],
    }),
    addCompanyLinks: builder.mutation<APIResponse, { profile_id: string, data: { company_profiles: string[] } }>({
      query: ({ profile_id, data }) => ({
        url: `/my/companies/${profile_id}/company-links`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CompanyLink', id: args.profile_id }],
    }),
    removeCompanyLink: builder.mutation<APIResponse, { profile_id: string, link_id: string }>({
      query: ({ profile_id, link_id }) => ({
        url: `/my/companies/${profile_id}/company-links/${link_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'CompanyLink', id: args.profile_id }],
    }),

    /**
     * A company's linked contacts as flat rows for the shared DataList, so the tab reuses the
     * contacts column registry. `column_filters` is JSON-encoded to match the other lean lists.
     */
    getCompanyContactLinks: builder.query<
      PaginatedResponse<ICompanyContactLinkListItem>,
      {
        profile_id: string;
        params: PageOptions & {
          search_query?: string;
          sort_by?: string;
          sort_order?: SortOrder;
          column_filters?: Record<string, string>;
        };
      }
    >({
      query: ({ profile_id, params }) => {
        const { column_filters, ...rest } = params;
        return {
          url: `/my/companies/${profile_id}/contact-links`,
          params: {
            ...rest,
            ...(column_filters && Object.keys(column_filters).length
              ? { column_filters: JSON.stringify(column_filters) }
              : {}),
          },
        };
      },
      providesTags: (_res, _err, args) => [{ type: 'CompanyContactLink', id: args.profile_id }],
    }),

    // Org tree: full, non-paginated list of the company's contacts (each carrying
    // reports_to_contact_id) for building the org chart client-side. Shares the
    // CompanyContactLink cache tag so a manager change invalidates and refetches it.
    getCompanyOrgChart: builder.query<Contact[], { profile_id: string }>({
      query: ({ profile_id }) => ({ url: `/my/companies/${profile_id}/org-chart` }),
      providesTags: (_res, _err, args) => [{ type: 'CompanyContactLink', id: args.profile_id }],
    })
  }),

});

export const {
  useGetOrgConnectionsQuery,
  useLazyGetOrgConnectionsQuery,
  useActionConnectionRequestMutation,
  useLazyGetConnectionRequestByIdQuery,
  useInitiateConnectionRequestMutation,
  useUnlinkConnectionMutation,
  useCancelConnectionRequestMutation,
  useGetOutgoingConnectionRequestsQuery,
  useGetIncomingConnectionRequestsQuery,
  useLazyGetIncomingConnectionRequestsQuery,
  useLazyGetOutgoingConnectionRequestsQuery,
  useUpdateConnectionSettingsMutation,
  useLazyGetConnectionByIdQuery,
  useLazyGetConnectionRequestByTokenQuery,
  useGetConnectionRequestByTokenQuery,
  useGetConnectionActivitiesQuery,

  useGetInteractedOrganisationsQuery,
  useGetConnectionJobsQuery,
  useGetConnectionApplicationsQuery,
  useGetConnectionContactsQuery,
  useGetConnectionProfileByIdQuery,
  useGetConnectionRequestsQuery,

  useGetMyCompaniesQuery,
  useGetMyCompaniesLeanQuery,
  useGetCompanyByIdQuery,
  useGetCompanyActivitiesQuery,
  useGetCompanyJobsQuery,
  useGetCompanyContactsQuery,
  useGetCompanyApplicationsQuery,
  useCreateLocalCompanyMutation,
  useUpdateLocalCompanyMutation,
  useUpdateCompanyAccountOwnerMutation,
  useBulkCompanyImportMutation,

  useGetCompanyLinksQuery,
  useAddCompanyLinksMutation,
  useRemoveCompanyLinkMutation,

  useGetCompanyDocumentsQuery,
  useCreateCompanyDocumentMutation,
  useRemoveCompanyDocumentMutation,
  useCopyContactDocumentToCompanyMutation,

  useGetCompanyContactLinksQuery,

  useGetCompanyOrgChartQuery

} = connectionsApi;
